import { useTheme, useMediaQuery } from '@mui/material';

/**
 * Enhanced responsive utilities for screen size detection and management
 */

// Screen size categories
export type ScreenCategory = 
  | 'mobile-portrait' 
  | 'mobile-landscape' 
  | 'tablet' 
  | 'small-desktop' 
  | 'desktop' 
  | 'large-desktop';

// Metric card sizing configuration
export interface MetricCardSizing {
  titleSize: string;
  valueSize: string;
  subtitleSize: string;
  dataSize: string;
  padding: number;
  gap: number;
  iconSize: number;
  showSubtitle: boolean;
  stackThreshold: number; // Number of data items before stacking
}

/**
 * Custom hook for comprehensive responsive breakpoint detection
 */
export const useResponsiveBreakpoints = () => {
  const theme = useTheme();
  
  // Standard Material-UI breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallDesktop = useMediaQuery(theme.breakpoints.down('lg'));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl'));
  
  // Enhanced granular detection
  const isMobilePortrait = useMediaQuery('(max-width: 480px)');
  const isMobileLandscape = useMediaQuery('(min-width: 481px) and (max-width: 767px)');
  const isTabletPortrait = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  
  // Screen characteristics
  const isHighDensity = useMediaQuery('(-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)');
  const isTouchDevice = useMediaQuery('(pointer: coarse)');
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  
  // Get current screen category
  const getScreenCategory = (): ScreenCategory => {
    if (isMobilePortrait) return 'mobile-portrait';
    if (isMobileLandscape) return 'mobile-landscape';
    if (isTabletPortrait) return 'tablet';
    if (isSmallDesktop) return 'small-desktop';
    if (isLargeDesktop) return 'large-desktop';
    return 'desktop';
  };
  
  // Get optimal grid columns for different layouts
  const getGridColumns = (layoutType: 'dashboard' | 'standard' | 'compact' = 'standard') => {
    const category = getScreenCategory();
    
    switch (layoutType) {
      case 'dashboard': // For dashboard 5-column layout
        if (category === 'mobile-portrait') return 1;
        if (category === 'mobile-landscape') return 2;
        if (category === 'tablet') return 3;
        if (category === 'small-desktop') return 4;
        return 5;
        
      case 'compact': // For compact layouts
        if (isMobile) return 1;
        if (isTablet) return 2;
        return 3;
        
      default: // Standard layout
        if (isMobile) return 1;
        if (isTablet) return 2;
        return 3;
    }
  };
  
  // Get MetricCard sizing based on screen size and context
  const getMetricCardSizing = (context: 'dashboard' | 'standard' = 'standard'): MetricCardSizing => {
    const category = getScreenCategory();
    
    if (context === 'dashboard') {
      // Dashboard context (narrow columns)
      switch (category) {
        case 'mobile-portrait':
          return {
            titleSize: '0.8rem',
            valueSize: '1rem',
            subtitleSize: '0.7rem',
            dataSize: '0.65rem',
            padding: 1.5,
            gap: 1.5,
            iconSize: 6,
            showSubtitle: true,
            stackThreshold: 2,
          };
        case 'mobile-landscape':
          return {
            titleSize: '0.75rem',
            valueSize: '0.9rem',
            subtitleSize: '0.65rem',
            dataSize: '0.6rem',
            padding: 1.2,
            gap: 1,
            iconSize: 6,
            showSubtitle: true,
            stackThreshold: 2,
          };
        case 'tablet':
        case 'small-desktop':
          return {
            titleSize: '0.7rem',
            valueSize: '0.8rem',
            subtitleSize: '0.6rem',
            dataSize: '0.58rem',
            padding: 1,
            gap: 0.5,
            iconSize: 5,
            showSubtitle: false, // Hide subtitle in very narrow contexts
            stackThreshold: 1,
          };
        default:
          return {
            titleSize: '0.85rem',
            valueSize: '1rem',
            subtitleSize: '0.7rem',
            dataSize: '0.65rem',
            padding: 1.5,
            gap: 1,
            iconSize: 6,
            showSubtitle: true,
            stackThreshold: 2,
          };
      }
    } else {
      // Standard context (wider columns)
      switch (category) {
        case 'mobile-portrait':
          return {
            titleSize: '0.95rem',
            valueSize: '1.25rem',
            subtitleSize: '0.8rem',
            dataSize: '0.75rem',
            padding: 2,
            gap: 1.5,
            iconSize: 8,
            showSubtitle: true,
            stackThreshold: 3,
          };
        case 'mobile-landscape':
          return {
            titleSize: '0.9rem',
            valueSize: '1.15rem',
            subtitleSize: '0.75rem',
            dataSize: '0.7rem',
            padding: 1.8,
            gap: 1.2,
            iconSize: 8,
            showSubtitle: true,
            stackThreshold: 3,
          };
        case 'tablet':
          return {
            titleSize: '0.95rem',
            valueSize: '1.2rem',
            subtitleSize: '0.8rem',
            dataSize: '0.75rem',
            padding: 2,
            gap: 1.5,
            iconSize: 8,
            showSubtitle: true,
            stackThreshold: 4,
          };
        default:
          return {
            titleSize: '1rem',
            valueSize: '1.3rem',
            subtitleSize: '0.85rem',
            dataSize: '0.8rem',
            padding: 2.5,
            gap: 2,
            iconSize: 10,
            showSubtitle: true,
            stackThreshold: 5,
          };
      }
    }
  };
  
  // Get responsive spacing
  const getSpacing = (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md') => {
    const multipliers = {
      xs: 0.5,
      sm: 1,
      md: 2,
      lg: 3,
      xl: 4,
    };
    
    const baseMultiplier = multipliers[size];
    
    // Reduce spacing on mobile
    if (isMobile) {
      return theme.spacing(baseMultiplier * 0.75);
    }
    
    return theme.spacing(baseMultiplier);
  };
  
  // Check if layout should stack
  const shouldStack = (itemCount: number, context: 'dashboard' | 'standard' = 'standard') => {
    const sizing = getMetricCardSizing(context);
    return isMobile || (isSmallDesktop && itemCount > sizing.stackThreshold);
  };
  
  // Get container constraints
  const getContainerConstraints = () => ({
    maxWidth: isMobile ? '100vw' : isTablet ? '95vw' : 'lg',
    padding: isMobile ? 1 : isTablet ? 1.5 : 2,
    margin: isMobile ? 0 : 'auto',
  });
  
  return {
    // Basic breakpoints
    isMobile,
    isTablet,
    isSmallDesktop,
    isLargeDesktop,
    
    // Enhanced detection
    isMobilePortrait,
    isMobileLandscape,
    isTabletPortrait,
    isDesktop,
    
    // Device characteristics
    isHighDensity,
    isTouchDevice,
    prefersReducedMotion,
    
    // Utility functions
    getScreenCategory,
    getGridColumns,
    getMetricCardSizing,
    getSpacing,
    shouldStack,
    getContainerConstraints,
    
    // Current values
    screenCategory: getScreenCategory(),
    spacing: {
      xs: getSpacing('xs'),
      sm: getSpacing('sm'),
      md: getSpacing('md'),
      lg: getSpacing('lg'),
      xl: getSpacing('xl'),
    },
  };
};

/**
 * Utility for creating responsive styles
 */
export const createResponsiveStyles = (theme: any) => ({
  // Common responsive patterns
  responsiveText: {
    fontSize: 'clamp(0.8rem, 2vw, 1rem)',
    lineHeight: 1.4,
  },
  responsivePadding: {
    padding: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1.5),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2),
    },
  },
  responsiveMargin: {
    margin: theme.spacing(0.5),
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(1.5),
    },
  },
  mobileFullWidth: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
    },
  },
  hideOnMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  showOnlyMobile: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
});

export default useResponsiveBreakpoints;
